// ---------------------------------------------------------------------------
// Accessify Widget — All-in-One Entry Point
// ---------------------------------------------------------------------------
// Single script tag integration:
//   <script src="widget.js" data-accessify defer></script>
//
// That's it. No init call needed. The widget auto-initializes.
// Optional data attributes:
//   data-lang, data-position, data-theme, data-features, data-openrouter-key,
//   data-site-key, data-proxy-url, data-compact, data-z-index, data-statement-url
// ---------------------------------------------------------------------------

import { init, destroy, getActiveConfig } from './index';
import type { AccessifyConfig } from './types';

/**
 * Attach a live `config` getter to whatever function is currently acting
 * as `window.Accessify`. R9-4 caught this on production: the queue-API
 * gets replaced by a plain function inside `autoInit`, and that function
 * has no `config` property — so `window.Accessify.config` returned
 * undefined for any external integration trying to read site-level state.
 */
function attachConfigGetter(target: any): void {
  if (!target) return;
  try {
    Object.defineProperty(target, 'config', {
      configurable: true,
      enumerable: true,
      get: () => getActiveConfig(),
    });
  } catch {
    // defineProperty can throw on frozen objects — ignore, the consumer
    // will simply see `config: undefined`, no worse than the old behaviour.
  }
}

const currentScript = document.currentScript as HTMLScriptElement | null;

// ---------------------------------------------------------------------------
// Read config from data-* attributes on the script tag
// ---------------------------------------------------------------------------

function getScriptConfig(): Partial<AccessifyConfig> {
  if (!currentScript) return {};
  const d = currentScript.dataset;
  const config: Partial<AccessifyConfig> = {};

  if (d.openrouterKey) config.openRouterKey = d.openrouterKey;
  if (d.siteKey) config.siteKey = d.siteKey;
  if (d.proxyUrl) config.proxyUrl = d.proxyUrl;
  if (d.lang) config.lang = d.lang;
  if (d.position) config.position = d.position as AccessifyConfig['position'];
  if (d.theme) config.theme = d.theme as AccessifyConfig['theme'];
  if (d.features) config.features = d.features.split(',').map((value) => value.trim()).filter(Boolean);
  if (d.statementUrl) config.statementUrl = d.statementUrl;
  if (d.compact === 'true') config.compact = true;
  if (d.zIndex) config.zIndex = parseInt(d.zIndex, 10);

  // Default proxy URL if siteKey is present but no proxyUrl
  if (config.siteKey && !config.proxyUrl) {
    config.proxyUrl = 'https://api.goaccessify.de';
  }

  return config;
}

// ---------------------------------------------------------------------------
// Queue API (for manual calls before auto-init)
// ---------------------------------------------------------------------------

const queue: any[][] = [];

// R13-3: Stateful Guard gegen Doppel-init(). `accessify-root`-Check reicht
// nicht, weil init() async ist und das DOM-Mount erst nach dem Server-Config-
// Fetch passiert. In der Zwischenzeit kann der Loader-Queue-Init und der
// autoInit-Branch beide feuern.
let _initStarted = false;

function exec(command: string, ...args: any[]) {
  const commands: Record<string, Function> = {
    init: (cfg: any) => {
      // R13-3: zweites init() unterdrücken — Code-Pfad könnte von Loader-Queue
      // ODER autoInit kommen, beide dürfen nicht gleichzeitig mounten.
      if (_initStarted && document.getElementById('accessify-root')) return;
      _initStarted = true;
      init({ ...getScriptConfig(), ...cfg });
    },
    destroy,
  };
  commands[command]?.(...args);
}

(window as any).__accessify_exec = exec;

const api = function(command: string, ...args: any[]) {
  if (api._loaded) {
    exec(command, ...args);
  } else {
    queue.push([command, ...args]);
  }
} as any;

api.q = queue;
api._loaded = false;

// Warn when an unrelated global is being clobbered. The widget API is a
// callable function with `.q` (queue array) and `_loaded`; anything else
// suggests another script already claimed `window.Accessify` for a
// different purpose.
const existing = (window as any).Accessify;
if (existing && (typeof existing !== 'function' || (!('q' in existing) && !('_loaded' in existing)))) {
  console.warn(
    '[Accessify] window.Accessify was already defined by another script; ' +
      'the widget API has now replaced it. This may break the other script.'
  );
}
(window as any).Accessify = api;
attachConfigGetter(api);

// Backward compatibility — `A11yPlus` is the legacy name from the pre-rebrand
// integration snippets that are still embedded in old customer pages. The
// alias is intentionally a Proxy/getter so each access logs a deprecation
// notice once per session: new integrations should use `Accessify`.
let a11yPlusWarned = false;
Object.defineProperty(window as any, 'A11yPlus', {
  configurable: true,
  get() {
    if (!a11yPlusWarned) {
      a11yPlusWarned = true;
      console.warn(
        '[Accessify] window.A11yPlus is deprecated and will be removed in a ' +
          'future release. Replace it with window.Accessify.'
      );
    }
    return (window as any).Accessify;
  },
  set(value) {
    a11yPlusWarned = true;
    Object.defineProperty(window as any, 'A11yPlus', { value, writable: true, configurable: true });
  },
});

// ---------------------------------------------------------------------------
// Auto-initialize
// ---------------------------------------------------------------------------

function autoInit() {
  api._loaded = true;

  // Process any queued calls first — wenn der Loader bereits `init` gequeued
  // hat, läuft das hier durch und setzt `_initStarted`.
  if (queue.length > 0) {
    queue.forEach((args: any[]) => {
      const [cmd, ...rest] = args;
      if (cmd === 'init') _initStarted = true;
      if (typeof cmd === 'string') exec(cmd, ...rest);
    });
    queue.length = 0;
  }

  // Auto-init nur wenn KEIN init bereits gestartet ist (egal ob via Queue
  // oder bereits gemountet).
  const config = getScriptConfig();
  const shouldAutoInit = Boolean(config.siteKey) || currentScript?.hasAttribute('data-accessify');
  const alreadyMounted = Boolean(document.getElementById('accessify-root'));
  if (shouldAutoInit && !_initStarted && !alreadyMounted) {
    _initStarted = true;
    (window as any).__accessify_dataConfig = config;
    init(config);
  }

  // Replace queue API with direct exec. The `config` property is wired up
  // through a live getter (attachConfigGetter) so it follows the active
  // module-level config instead of a stale snapshot.
  const directApi = function(cmd: string, ...args: any[]) { exec(cmd, ...args); };
  (directApi as any)._loaded = true;
  attachConfigGetter(directApi);
  (window as any).Accessify = directApi;
  (window as any).A11yPlus = directApi; // backward compat
}

if (document.readyState === 'complete' || document.readyState === 'interactive') {
  setTimeout(autoInit, 1);
} else {
  document.addEventListener('DOMContentLoaded', autoInit);
}
